/**
 * What a frame's menu items do (Part 04 decision 12). The wall card menu,
 * the opened item's ⋯ and the canvas frame and multi-selection menus all
 * call these; which items a menu shows lives in `frame-menu-model.ts`.
 * Each action reports its own failure with a toast.
 */
import { isNotFoundError } from "@/api/client";
import * as frameListing from "@/api/frame-listing";
import * as spacesApi from "@/api/spaces";
import {
  copyFrameText,
  frameContentToClipboardText,
  frameHasCopyText,
} from "@/components/shapes/shared/frame-copy";
import {
  frameDownloadOptions,
  saveFrameDownload,
  type FrameDownloadOption,
} from "@/components/shapes/shared/frame-download";
import { retargetDesktopPin } from "@/desktop/pin/desktop-pin-actions";
import { translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { copyToClipboard } from "@/lib/copy-to-clipboard";
import type { FrameData } from "@/lib/frame-data";
import { ensureFrameDetail } from "@/lib/frame-detail-requests";
import { applyFramePatch, discardFrames } from "@/lib/frame-ingest";
import { leavePreview, returnToPreview } from "@/lib/frame-preview-removal";
import { enterPresentationMode } from "@/lib/presentation-mode";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { useSelectionStore } from "@/stores/selection-store";
import { foreignWallFrame, useWallScopeStore } from "@/stores/wall-scope-store";

function currentProjectId(): string | null {
  return useProjectStore.getState().currentProject?.id ?? null;
}

function errorToast(id: string, title: string, description: string): void {
  useNotificationStore
    .getState()
    .addSystemToast({ id, title, description, level: "error" });
}

/** Say whether a Copy reached the clipboard; `failure` says why not. */
function reportCopy(id: string, copied: boolean, failure?: string): boolean {
  useNotificationStore.getState().addSystemToast(
    copied
      ? { id, title: translate(commonMessages, "copied"), level: "info" }
      : {
          id,
          title: translate(framesSharedMessages, "copyFailed"),
          description: failure ?? translate(framesSharedMessages, "tryAgain"),
          level: "error",
        },
  );
  return copied;
}

/**
 * Copy the text selected inside `root`, else the frame's whole content. A
 * selection or a full frame copies at once; a card-only frame's content
 * copies once its detail arrives (a failed detail has shown its toast).
 * Nothing to copy says so.
 */
export async function copyFrame(
  frame: FrameData,
  {
    root = null,
    selection = "",
  }: { root?: HTMLElement | null; selection?: string } = {},
): Promise<boolean> {
  const id = `frame-copy-${frame.frameId}`;
  let full = frame;
  // A selection copies as it is; only the whole content needs the detail.
  if (frame.contentState === "card" && !selection.trim()) {
    try {
      full = await ensureFrameDetail(frame.frameId);
    } catch {
      return false;
    }
  }
  if (!frameHasCopyText(full, root, selection)) {
    return reportCopy(
      id,
      false,
      translate(framesSharedMessages, "nothingToCopy"),
    );
  }
  return reportCopy(id, await copyFrameText(full, root, selection));
}

/** Copy a link the frame shows (a link frame's ⋯ Copy link). */
export async function copyFrameLink(
  frameId: string,
  url: string,
): Promise<boolean> {
  return reportCopy(`frame-copy-link-${frameId}`, await copyToClipboard(url));
}

/** Copy several frames' content; nothing when one detail fails to load. */
export async function copyFrames(
  frameIds: readonly string[],
): Promise<boolean> {
  const { framesById } = useFramesStore.getState();
  const known = frameIds.filter((id) => framesById[id] !== undefined);
  // A failed detail has shown its toast.
  const frames = await Promise.all(
    known.map((id) => ensureFrameDetail(id)),
  ).catch(() => null);
  if (!frames) return false;
  const text = frames
    .map((frame) => frameContentToClipboardText(frame))
    .filter(Boolean)
    .join("\n\n");
  if (!text) {
    return reportCopy(
      "frames-copy",
      false,
      translate(framesSharedMessages, "nothingToCopy"),
    );
  }
  return reportCopy("frames-copy", await copyToClipboard(text));
}

/**
 * Save the frame as `option`, or as its one format when no option is
 * picked; a card-only frame's content loads first (a failed detail has
 * shown its toast).
 */
export async function downloadFrame(
  frame: FrameData,
  {
    option,
    root = null,
  }: { option?: FrameDownloadOption; root?: HTMLElement | null } = {},
): Promise<boolean> {
  const failed = (description: string) => {
    errorToast(
      `frame-download-failed-${frame.frameId}`,
      translate(framesSharedMessages, "downloadFailed"),
      description,
    );
    return false;
  };
  let chosen = option;
  if (!chosen) {
    let full = frame;
    if (frame.contentState === "card") {
      try {
        full = await ensureFrameDetail(frame.frameId);
      } catch {
        return false;
      }
    }
    chosen = frameDownloadOptions(full, { rendered: root !== null })[0];
    if (!chosen) {
      return failed(translate(framesSharedMessages, "nothingToDownload"));
    }
  }
  try {
    return await saveFrameDownload(chosen, root);
  } catch (error) {
    return failed(
      error instanceof Error
        ? error.message
        : translate(framesSharedMessages, "tryAgain"),
    );
  }
}

function moveErrorDescription(error: unknown): string {
  const apiError = error as { status?: number; data?: Record<string, unknown> };
  const responseError = apiError.data?.error;
  if (typeof responseError === "string" && responseError.trim()) {
    return responseError;
  }
  if (apiError.status === 404)
    return translate(framesSharedMessages, "frameChangedOrRemoved");
  const detail = apiError.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (error instanceof TypeError)
    return translate(framesSharedMessages, "checkConnection");
  return translate(framesSharedMessages, "moveFailedDetail");
}

/**
 * Move the frames from `fromProjectId` (the project on screen unless named)
 * to another project; failures are reported once. Moved out of the project
 * on screen, each leaves the wall, the preview and the selection once the
 * server agrees. True when every frame moved.
 */
export async function moveFramesToProject(
  frameIds: readonly string[],
  toProjectId: string,
  fromProjectId: string | null = currentProjectId(),
): Promise<boolean> {
  if (
    !fromProjectId ||
    toProjectId === fromProjectId ||
    frameIds.length === 0
  ) {
    return false;
  }
  const results = await Promise.allSettled(
    frameIds.map((frameId) =>
      spacesApi.updateFrame(fromProjectId, frameId, { project: toProjectId }),
    ),
  );
  const moved = frameIds.filter(
    (_, index) => results[index]!.status === "fulfilled",
  );
  for (const frameId of moved) retargetDesktopPin(frameId, toProjectId);
  if (moved.length > 0 && fromProjectId === currentProjectId()) {
    const movedIds = new Set(moved);
    useSelectionStore.getState().retain((id) => !movedIds.has(id));
    leavePreview(moved);
    discardFrames(moved);
  }
  const failure = results.find(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (!failure) return true;
  errorToast(
    `frame-move-failed-${frameIds.join(",")}-${toProjectId}`,
    translate(framesSharedMessages, "moveFailed"),
    moveErrorDescription(failure.reason),
  );
  return false;
}

/**
 * Delete for good, through `projectId` (the project on screen unless
 * named): the frames leave the wall and the preview at once. A frame the
 * server no longer has counts as deleted. Any other failure is reported
 * once, and that frame comes back, into the preview too if nothing else
 * shows.
 */
export async function deleteFrames(
  frameIds: readonly string[],
  projectId: string | null = currentProjectId(),
): Promise<boolean> {
  if (!projectId || frameIds.length === 0) return false;
  const { framesById } = useFramesStore.getState();
  const removed = frameIds.flatMap((id) => framesById[id] ?? []);
  const previewed = leavePreview(frameIds);
  discardFrames(frameIds);
  const results = await Promise.allSettled(
    frameIds.map((id) => spacesApi.deleteFrame(projectId, id)),
  );
  const kept = new Set(
    frameIds.filter((_, index) => {
      const result = results[index]!;
      return result.status === "rejected" && !isNotFoundError(result.reason);
    }),
  );
  if (kept.size === 0) return true;
  useFramesStore
    .getState()
    .upsertFrames(removed.filter(({ frameId }) => kept.has(frameId)));
  returnToPreview(previewed.filter((id) => kept.has(id)));
  errorToast(
    frameIds.length === 1
      ? `frame-delete-failed-${frameIds[0]}`
      : "frames-delete-failed",
    translate(framesSharedMessages, "deleteFailed", { count: kept.size }),
    translate(framesSharedMessages, "checkConnection"),
  );
  return false;
}

/**
 * The latest archive write per frame. The next write is sent once it has
 * settled, so the server sees Archive and Undo in order, and only the latest
 * write's answer touches the frame.
 */
const archiveWrites = new Map<string, Promise<boolean>>();

function archiveToastId(frameId: string): string {
  return `frame-archive-${frameId}`;
}

interface ArchiveTarget {
  /** The frame's own project: the archive is written through it. */
  projectId: string;
  previous: string | null;
  /** Show an `archivedAt`; `server` when it is the server's answer. */
  show: (archivedAt: string | null, server: boolean) => void;
}

/**
 * Another project's card in Everything (`foreignWallFrame`, its own map),
 * or a frame of the current project: the frames store's, else `shown`, the
 * frame an opened surface shows without it there (a fresh docked chat);
 * `null` when unknown.
 */
function archiveTarget(
  frameId: string,
  shown: FrameData | undefined,
): ArchiveTarget | null {
  const foreign = foreignWallFrame(frameId);
  if (foreign) {
    if (!foreign.spaceId) return null;
    return {
      projectId: foreign.spaceId,
      previous: foreign.archivedAt ?? null,
      show: (archivedAt) =>
        useWallScopeStore.getState().patchForeignFrame(frameId, { archivedAt }),
    };
  }
  const frame = useFramesStore.getState().framesById[frameId] ?? shown;
  const projectId = currentProjectId();
  if (!frame || !projectId) return null;
  return {
    projectId,
    previous: frame.archivedAt ?? null,
    show: (archivedAt, server) =>
      applyFramePatch(
        frameId,
        { archived_at: archivedAt },
        { origin: server ? "server" : "local" },
      ),
  };
}

/**
 * Patch `archivedAt` at once (the wall feed, Spaces and the index drop or
 * bring back the card by membership), then save it through the frame's own
 * project; a failure reverts. `null` when the frame or the project is
 * unknown.
 */
function writeArchived(
  frameId: string,
  archive: boolean,
  shown?: FrameData,
): Promise<boolean> | null {
  const target = archiveTarget(frameId, shown);
  if (!target) return null;
  const { projectId, previous, show } = target;
  show(archive ? new Date().toISOString() : null, false);
  const save = archive
    ? frameListing.archiveFrame
    : frameListing.unarchiveFrame;
  const latest = () => archiveWrites.get(frameId) === write;
  const write: Promise<boolean> = (
    archiveWrites.get(frameId) ?? Promise.resolve(true)
  )
    .then(() => save(projectId, frameId))
    .then(
      (saved) => {
        if (latest()) show(saved.archived_at ?? null, true);
        return true;
      },
      () => {
        if (latest()) show(previous, false);
        errorToast(
          archiveToastId(frameId),
          archive
            ? translate(framesSharedMessages, "archiveFailed")
            : translate(framesSharedMessages, "unarchiveFailed"),
          translate(framesSharedMessages, "checkConnection"),
        );
        return false;
      },
    )
    .finally(() => {
      if (latest()) archiveWrites.delete(frameId);
    });
  archiveWrites.set(frameId, write);
  return write;
}

/**
 * Take the frames off the wall, out of Spaces, search and the preview, with
 * one Undo toast for all of them; true when every known frame's archive was
 * saved. `shown`: frames an opened surface shows outside the frames store.
 */
export function archiveFrames(
  frameIds: readonly string[],
  shown: readonly FrameData[] = [],
): Promise<boolean> {
  const shownFrame = (frameId: string) =>
    shown.find((frame) => frame.frameId === frameId);
  const archived = frameIds.flatMap((frameId) => {
    const saving = writeArchived(frameId, true, shownFrame(frameId));
    return saving ? [{ frameId, saving }] : [];
  });
  if (archived.length === 0) return Promise.resolve(false);
  // Archived frames leave the wall, so they leave its selection and the
  // preview too; a failed archive brings them back.
  const archivedIds = new Set(archived.map(({ frameId }) => frameId));
  useSelectionStore.getState().retain((id) => !archivedIds.has(id));
  const previewed = leavePreview([...archivedIds]);
  const single = archived.length === 1 ? archived[0]!.frameId : null;
  useNotificationStore.getState().addSystemToast(
    {
      // Each batch keeps its own Undo.
      id: single
        ? archiveToastId(single)
        : `frames-archive-${[...archivedIds].join(",")}`,
      title: single
        ? translate(framesSharedMessages, "archived")
        : translate(framesSharedMessages, "archivedItems", {
            count: archived.length,
          }),
      level: "info",
      actionLabel: translate(framesSharedMessages, "undo"),
      actionTestId: "frame-archive-undo",
    },
    () => {
      for (const { frameId } of archived) {
        void unarchiveFrame(frameId, shownFrame(frameId));
      }
    },
  );
  return Promise.all(archived.map(({ saving }) => saving)).then((saved) => {
    const failed = new Set(
      archived.flatMap(({ frameId }, index) => (saved[index] ? [] : frameId)),
    );
    returnToPreview(previewed.filter((id) => failed.has(id)));
    return saved.every(Boolean);
  });
}

/** Bring an archived frame back to the wall (`shown` as in `archiveFrames`). */
export function unarchiveFrame(
  frameId: string,
  shown?: FrameData,
): Promise<boolean> {
  return writeArchived(frameId, false, shown) ?? Promise.resolve(false);
}

/** Expand: the frames side by side as deliverables. */
export function expandFrames(frameIds: readonly string[]): void {
  enterPresentationMode("deliverables", [...frameIds]);
}
